// ─────────────────────────────────────────────
//  Case phases — pure domain for the mobile Room Status screen
//  (design: docs/specs/2026-09-07-mobile-room-status-design.md §3 / §3.1).
//
//  A provider stamps four clinical phases for their own use; the board updates
//  itself as a side effect, instead of a human remembering to move a room to
//  Active and then Closing. That side effect is the whole risk of this feature:
//  a half-used or mis-mapped status feed is WORSE than none, because the board
//  is then confidently wrong — and Induction and Emergence are the two busiest,
//  highest-risk moments of an anesthetic, i.e. exactly when a tap is most
//  likely to be late, missed, or wrong. So this module is deliberately dumb:
//  one total function, no clock, no I/O, and "write nothing" as a first-class
//  answer rather than a best guess.
//
//  ── Why a separate layer and not new statuses ──
//  ROOM_STATUSES (C-002, domain/status.js) is canonical: RoomCard reads its
//  META for colour and ACTIVE_STATUSES decides "is this room live", which is
//  the denominator the Doctors Needed box counts from. A phase is not a sixth
//  status, exactly as a condition is not a sixth status (see the precedent
//  spelled out in domain/conditions.js). A single status string also cannot
//  hold eight timestamps (4 stamps + 4 acknowledgements), which is why the
//  stamps live on or_cases.phases and only their *consequence* is mapped here.
//
//  ── Why there are no colours in this file ──
//  PHASE_META carries labels only. Board colour stays owned by status.js META,
//  so a phase can never introduce a sixth colour or drift from the card. (Where
//  a domain module genuinely needs colour it mirrors hex with a comment — see
//  conditions.js — but here needing one would be the bug.)
//
//  ── Time in / Time out are boundaries, not phases ──
//  They are not members of CASE_PHASES, carry no acknowledgement, and render in
//  the header rather than the grid. They DO move (or deliberately fail to move)
//  the board status, so they share the one mapping function below.
// ─────────────────────────────────────────────

import { normalizeStatus } from "./status.js";

// The four clinical phases, in tap order (D1/D2). Ids are stored verbatim as
// keys of or_cases.phases, so renaming one is a data migration, not a rename.
export const CASE_PHASES = ["to_room", "induction", "closing", "emergence"];

// "Going to the room" is the CRNA still in preop and heading over; "Induction"
// is the patient going to sleep (D2). The owner's words — do not paraphrase
// them into clinical jargon on the screen.
export const PHASE_META = {
  // Owner, 2026-09-07: "change going to the room to: To room". LABEL ONLY —
  // the id `to_room` is a key of `or_cases.phases` on live documents, so
  // renaming THAT is a data migration, not a rename.
  to_room:   { label: "To room" },
  induction: { label: "Induction" },
  closing:   { label: "Closing" },
  emergence: { label: "Waking up" },
};

// The two case boundaries. Kept separate from CASE_PHASES so that anything
// iterating the acknowledgement grid physically cannot render an Ack column
// for a boundary (D4: an acknowledgement is a receipt for a phase).
export const CASE_EVENTS = ["time_in", "time_out"];

export const CASE_EVENT_META = {
  time_in:  { label: "Time in" },
  time_out: { label: "Time out" },
};

// Pressing "New case" (D6) archives the open case and opens the next one. It
// is an action rather than a stamped boundary — hence not in CASE_EVENTS — but
// it is the moment the room's status changes, so it is mapped here with
// everything else. Nothing outside this file may recompute a board status.
export const NEW_CASE_EVENT = "new_case";

export const phaseLabel = (phaseId) => PHASE_META[phaseId]?.label || "";

// ─────────────────────────────────────────────
//  phaseStatus(eventId, currentStatus) → status | null
//
//  THE single phase → board status mapping (§3.1). `null` means *write
//  nothing*: the caller must skip updateRoomStatus entirely rather than write
//  back the current value, so a no-op tap never touches updatedAt/updatedBy and
//  never races a manual status change made on the web board at the same moment.
//
//  currentStatus is routed through normalizeStatus() because live docs still
//  carry legacy values ("in_progress", "cancel"). A raw === "empty" comparison
//  looks right and quietly mis-handles them.
// ─────────────────────────────────────────────
export function phaseStatus(eventId, currentStatus) {
  // Fail closed on anything that is not one of our own literal ids — including
  // numbers, objects and case variants. Guessing here is what puts a wrong
  // colour on a board people trust.
  if (typeof eventId !== "string") return null;

  const status = normalizeStatus(currentStatus);

  switch (eventId) {
    // Promote an unused room to ready when someone starts walking to it, but
    // NEVER demote a room that is already live (or resurrect a cancelled one) —
    // a late or duplicate tap must not undo a real case in progress.
    case "to_room":
      return status === "empty" ? "ready" : null;

    // Patient in the room / going to sleep — the room is unambiguously live.
    case "time_in":
    case "induction":
      return "active";

    case "closing":
      return "closing";

    // Waking up is not leaving. The patient is still in the room, so the room
    // stays `closing`; writing anything here would empty the room early.
    case "emergence":
      return null;

    // ⚠️ time_out deliberately writes NOTHING. This is not a missing branch —
    // do not "fix" it. At Time out the patient is going to PACU, the room is
    // dirty and turnover runs 20–40 minutes, and the MD still owns the case
    // while the CRNA hands off. Writing "ready" would (a) tell everyone
    // scanning the board to send the next patient into a dirty room and
    // (b) drop the room out of ACTIVE_STATUSES, shrinking the Doctors Needed
    // denominator while its team is still working. It would also make "ready"
    // mean two opposite things, since that is what to_room writes. The room
    // stays occupied at `closing` until a new case is actually declared.
    // (This overrides the §3.1 table, which paired Time out with New case;
    // clinical review 2026-09-07 split them.)
    case "time_out":
      return null;

    // The one moment a room genuinely becomes available for the next patient.
    case NEW_CASE_EVENT:
      return "ready";

    // Unknown id: never guess. `cancelled` is unreachable from every branch
    // above and stays a manual action.
    default:
      return null;
  }
}

// ─────────────────────────────────────────────
//  phaseState(stamp, ack) → "pending" | "stamped" | "acked"
//
//  What one row of the grid should render (§6.2). Pure and shape-agnostic:
//  stamp/ack may be a Firestore Timestamp, a Date or an ISO string — only
//  presence matters, so this works identically against a snapshot and a
//  fixture.
//
//  An acknowledgement with no stamp reads as "pending", not "acked": a receipt
//  for something nobody stamped would render a completed phase with no time,
//  which is the confidently-wrong board this whole design exists to avoid.
// ─────────────────────────────────────────────
export function phaseState(stamp, ack) {
  if (!stamp) return "pending";
  return ack ? "acked" : "stamped";
}

// ─────────────────────────────────────────────
//  boardStatusActions(casePhaseMode) → status ids the BOARD card offers
//
//  The room card's footer changed on 2026-09-07: the room's own CRNA now gets
//  the four case phases there, writing the same `or_cases.phases` the phone
//  writes, instead of the raw status buttons.
//
//  ⚠️ CANCEL SURVIVES IN BOTH SHAPES. Cancelling a room is not a phase of a
//  case — it is the case NOT happening — so no phase button can express it, and
//  a board that cannot cancel a room is a board somebody has to phone about.
//
//  ⚠️ AND EVERYONE ELSE KEEPS ACTIVE AND CLOSING. An AIC runs the board without
//  standing in the room, and firestore.rules lets only the room's own team stamp
//  a case. Handing them four controls the server refuses, in place of the two
//  that work, would be a regression dressed as a feature.
// ─────────────────────────────────────────────
// ⚠️ KEYED ON THE ROOM'S OWN TEAM, NOT ON "MAY STAMP". These are two different
// questions and conflating them was a real regression: `canStampCasePhases`
// includes admin and AIC, so an AIC lost "→ Active" and "→ Closing" on EVERY
// card on the board and the only way left to turn a room green was to press
// Induction.
//
// An induction time is a billable, medico-legal, quality-reportable fact about
// a patient. Board management must never mint one. An AIC runs the board from
// the wall without standing in any room; they keep the two status controls that
// say what the BOARD thinks, and the phase buttons stay visible to them for the
// rooms where they genuinely are recording a case.
export function boardStatusActions(isRoomTeam) {
  return isRoomTeam ? ["cancelled"] : ["active", "closing", "cancelled"];
}

// Is this viewer actually in this room — its provider or its attending? The
// narrow question `boardStatusActions` needs, kept separate from the broader
// "may this account write a phase at all" that mirrors firestore.rules.
export function isRoomTeamMode(mode) {
  return mode === "provider" || mode === "supervisor";
}

// ─────────────────────────────────────────────
//  canStampCasePhases — may THIS viewer record a phase on THIS room's card?
//
//  ⚠️ THIS MIRRORS firestore.rules AND MUST KEEP MIRRORING IT. The `or_cases`
//  update clauses allow exactly four kinds of writer: the room's provider, the
//  room's attending, an admin, and the AIC of that facility. A client gate
//  NARROWER than the rules hides a control the server would have accepted —
//  which is what happened on 2026-09-07: the buttons were shown only to the
//  room's own CRNA, so the owner, an admin, saw no change at all on their own
//  board and reported the feature as not shipped. A gate WIDER than the rules
//  is the opposite failure, and worse: a button that always fails.
//
//  So it is written as one predicate, next to the mapping it belongs with,
//  rather than as a condition spelled out inside a component.
// ─────────────────────────────────────────────
export function canStampCasePhases({ mode = null, isAdmin = false, isAIC = false } = {}) {
  return mode === "provider" || mode === "supervisor" || Boolean(isAdmin) || Boolean(isAIC);
}

// ─────────────────────────────────────────────
//  currentPhaseId(phases) → the phase the case IS at, or null.
//
//  ⚠️ FURTHEST ALONG, NOT MOST RECENTLY STAMPED. If somebody stamps Closing and
//  then goes back to fill in an Induction they missed, the case is still at
//  Closing — the later WRITE is about an earlier moment. Ordering by timestamp
//  would walk the marker backwards and show a confidently-wrong board, which is
//  the failure this whole module exists to prevent.
//
//  ⚠️ AND A GAP STAYS A GAP. An unstamped phase before a stamped one is not
//  filled in or greyed out: a visible hole is an accurate statement that a stamp
//  was missed, and that is clinically useful information rather than a blemish.
// ─────────────────────────────────────────────
export function currentPhaseId(phases) {
  if (!phases || typeof phases !== "object") return null;
  for (let i = CASE_PHASES.length - 1; i >= 0; i -= 1) {
    const id = CASE_PHASES[i];
    const p = phases[id];
    if (p && (p.at || p.stampedAt)) return id;
  }
  return null;
}

// ─────────────────────────────────────────────
//  phaseChipLabel({ phaseId, status }) → the phase's word, or null.
//
//  The room card's chip shows the PHASE as its word and keeps the STATUS as its
//  colour, because `closing` and `emergence` share one status and the pill alone
//  cannot tell a supervisor which is happening.
//
//  ⚠️ BUT THE WORD MAY ONLY OVERRIDE WHILE IT STILL AGREES WITH THE COLOUR.
//  Press "→ Active" on a room whose last stamp was Closing and the status goes
//  green while the phase word stays "Closing" — a GREEN chip reading CLOSING,
//  where green means active on this board. The card would then be stating two
//  different things about one room, and contradicting the action the operator
//  just took: they pressed Active and nothing they can see changed.
//
//  So the phase speaks only when the room's status is still the one that phase
//  implies. A status that has moved away from it was moved BY HAND, and a
//  deliberate manual status is the more recent statement of the two.
//
//  `phaseStatus(...) === null` means "this phase writes no status" — emergence
//  and time_out — so it cannot disagree with anything and always speaks. That is
//  the case the whole override exists for: "Waking up" on an amber `closing`
//  room.
// ─────────────────────────────────────────────
// ⚠️ WHAT A PHASE *MEANS*, WHICH IS NOT WHAT IT WOULD *WRITE*.
//
// `phaseStatus` answers "what should I write, given where the room is now", and
// it is deliberately conditional: `to_room` promotes only an EMPTY room, so it
// returns null for a room already at `ready` — the room is ALREADY there.
//
// The chip is asking a different question: does this phase still describe the
// room? For that, a phase has one fixed meaning. Reusing `phaseStatus` conflated
// the two and produced both halves of the bug at once — "To room" stood down on
// a `ready` room where it was exactly right, and spoke on an `active` room where
// it was contradicting the colour.
//
// `null` here means "this phase never sets a status at all" — emergence, which
// is the case the whole override exists for ("Waking up" on an amber `closing`
// room).
const PHASE_MEANS_STATUS = {
  to_room: "ready",
  induction: "active",
  closing: "closing",
  emergence: null,
};

export function phaseChipLabel({ phaseId = null, status = null } = {}) {
  if (!phaseId) return null;
  const label = phaseLabel(phaseId);
  if (!label) return null;

  const key = normalizeStatus(status);

  // ⚠️ A CANCELLED OR EMPTY ROOM IS NOT MID-CASE. Whatever was once stamped, the
  // room's own state is now the more recent statement, and a purple CANCELLED
  // chip reading "To room" or "Waking up" is the board saying two things again.
  if (key === "cancelled" || key === "empty") return null;

  // A phase this module does not know cannot vouch for the room.
  if (!Object.prototype.hasOwnProperty.call(PHASE_MEANS_STATUS, phaseId)) return null;

  const means = PHASE_MEANS_STATUS[phaseId];

  // Emergence sets no status by design, so it cannot contradict one — and it is
  // the reason the chip prefers the phase at all, since `closing` and
  // `emergence` share the `closing` colour and only the word tells them apart.
  if (means === null) return label;

  return means === key ? label : null;
}
